import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Icon from "../AppIcon";

const Breadcrumb = ({ customItems = null }) => {
  const location = useLocation();
  const params = useParams();

  const generateBreadcrumbs = () => {
    if (customItems) {
      return customItems;
    }

    const pathSegments = location?.pathname
      ?.split("/")
      ?.filter((segment) => segment);
    const breadcrumbs = [
      { label: "Dashboard", path: "/dashboard", icon: "Home" },
    ];

    const routeMap = {
      "create-event": { label: "Create Event", icon: "Plus" },
      events: { label: "Events", icon: "Calendar" },
      analytics: { label: "Analytics", icon: "BarChart3" },
      profile: { label: "Profile", icon: "User" },
      settings: { label: "Settings", icon: "Settings" },
      "add-expense": { label: "Add Expense", icon: "Plus" },
      budgets: { label: "Budgets", icon: "Target" },
      "expenses/:id": { label: "Expense Details", icon: "FileText" },
      "expenses": { label: "Expenses", icon: "List" },
      reports: { label: "Reports", icon: "BarChart3" },
    };

    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      const path = "/" + pathSegments.slice(0, i + 1).join("/");
      const routeInfo = routeMap[segment];
      
      // Check if this is a dynamic segment (like an ID)
      const isDynamicSegment = /^\d+$/.test(segment) || segment.startsWith(':');
      const parentSegment = i > 0 ? pathSegments[i - 1] : null;

      if (isDynamicSegment) {
        // For dynamic segments like IDs, we still want to add the parent route first if it's not already added
        if (parentSegment && routeMap[parentSegment] && breadcrumbs.every(b => b.path !== `/${parentSegment}`)) {
          const parentRouteInfo = routeMap[parentSegment];
          breadcrumbs.push({
            label: parentRouteInfo.label,
            path: `/${parentSegment}`,
            icon: parentRouteInfo.icon,
          });
        }
        
        // Then add the dynamic segment (the ID)
        let icon = "Hash"; // Default icon for IDs
        if (parentSegment === 'analytics') {
          icon = "BarChart3"; // Use analytics icon for analytics/:type
        }
        breadcrumbs.push({
          label: segment, // This will be the actual ID value
          path: path,
          icon: icon,
        });
      } else if (routeInfo) {
        // For static route segments, just add them normally
        breadcrumbs.push({
          label: routeInfo.label,
          path: path,
          icon: routeInfo.icon,
        });
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumbs on dashboard or if only one item
  if (location?.pathname === "/dashboard" || breadcrumbs?.length <= 1) {
    return null;
  }

  return (
    <nav
      className="flex items-center space-x-2 text-sm text-muted-foreground mb-6"
      aria-label="Breadcrumb navigation"
    >
      {breadcrumbs?.map((crumb, index) => (
        <React.Fragment key={crumb?.path}>
          {index > 0 && (
            <Icon
              name="ChevronRight"
              size={14}
              className="text-muted-foreground/50"
              aria-hidden="true"
            />
          )}

          {index === breadcrumbs?.length - 1 ? (
            // Current page - not clickable
            <span
              className="flex items-center space-x-1 text-foreground font-medium"
              aria-current="page"
            >
              <Icon name={crumb?.icon} size={14} />
              <span>{crumb?.label}</span>
            </span>
          ) : (
            // Clickable breadcrumb
            <Link
              to={crumb?.path}
              className="flex items-center space-x-1 transition-micro hover:text-foreground focus:outline-none focus:text-foreground"
            >
              <Icon name={crumb?.icon} size={14} />
              <span>{crumb?.label}</span>
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

export { Breadcrumb };
